import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';

// Função para formatar telefone: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
const formatPhone = (value) => {
  if (!value) return '';
  
  // Remove tudo que não é número
  const digits = value.replace(/\D/g, '');
  
  // Remove código do país 55 se presente
  const cleanDigits = digits.startsWith('55') && digits.length >= 12 
    ? digits.slice(2) 
    : digits;
  
  // Aplica a máscara conforme a quantidade de dígitos
  if (cleanDigits.length <= 2) {
    return cleanDigits.length > 0 ? `(${cleanDigits}` : '';
  } else if (cleanDigits.length <= 6) {
    return `(${cleanDigits.slice(0, 2)}) ${cleanDigits.slice(2)}`;
  } else if (cleanDigits.length <= 10) {
    return `(${cleanDigits.slice(0, 2)}) ${cleanDigits.slice(2, 6)}-${cleanDigits.slice(6)}`;
  } else {
    // 11 dígitos (celular)
    return `(${cleanDigits.slice(0, 2)}) ${cleanDigits.slice(2, 7)}-${cleanDigits.slice(7, 11)}`;
  }
};

// Função para formatar CPF: XXX.XXX.XXX-XX
const formatCPF = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

function PacienteModal({ paciente, open, onClose, onSave }) {
  const [formData, setFormData] = useState({
    nome_completo: '',
    cpf: '',
    data_nascimento: '',
    telefone: '',
    email: '',
    convenio: 'particular',
    observacoes: ''
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (paciente) {
      setFormData({
        nome_completo: paciente.nome_completo || '',
        cpf: formatCPF(paciente.cpf || ''),
        data_nascimento: paciente.data_nascimento || '',
        telefone: formatPhone(paciente.telefone || ''),
        email: paciente.email || '',
        convenio: paciente.convenio || 'particular',
        observacoes: paciente.observacoes || ''
      });
    }
  }, [paciente]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome_completo.trim()) {
      newErrors.nome_completo = 'Nome obrigatório';
    }
    
    // Valida formato (XX) XXXX-XXXX ou (XX) XXXXX-XXXX
    if (formData.telefone && !/^\(\d{2}\) \d{4,5}-\d{4}$/.test(formData.telefone)) {
      newErrors.telefone = 'Formato: (11) 98765-4321';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    handleInputChange('telefone', formatted);
  };

  const handleSave = () => {
    if (!validateForm()) return;

    onSave(paciente.id, formData);
  };

  if (!paciente) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Detalhes do Paciente</DialogTitle>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            data-testid="close-modal-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </DialogHeader>
        
        <div className="mt-6 space-y-6">
          {/* Nome Completo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Completo <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.nome_completo}
              onChange={(e) => handleInputChange('nome_completo', e.target.value)}
              className={errors.nome_completo ? 'border-red-500' : ''}
              data-testid="input-modal-nome"
            />
            {errors.nome_completo && (
              <p className="text-red-500 text-xs mt-1">{errors.nome_completo}</p>
            )}
          </div>

          {/* CPF e Data de Nascimento */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CPF</label>
              <Input
                value={formData.cpf}
                onChange={(e) => handleInputChange('cpf', formatCPF(e.target.value))}
                placeholder="000.000.000-00"
                data-testid="input-modal-cpf"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Nascimento
              </label>
              <Input
                type="date"
                value={formData.data_nascimento}
                onChange={(e) => handleInputChange('data_nascimento', e.target.value)}
                data-testid="input-modal-nascimento"
              />
            </div>
          </div>

          {/* Telefone e Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
              <Input
                value={formData.telefone}
                onChange={handlePhoneChange}
                placeholder="(11) 98765-4321"
                maxLength={16}
                className={errors.telefone ? 'border-red-500' : ''}
                data-testid="input-modal-telefone"
              />
              {errors.telefone && (
                <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-mail</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="email@exemplo.com"
                className={errors.email ? 'border-red-500' : ''}
                data-testid="input-modal-email"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          {/* Convênio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Convênio</label>
            <Select value={formData.convenio} onValueChange={(value) => handleInputChange('convenio', value)}>
              <SelectTrigger data-testid="select-modal-convenio">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="particular">Particular</SelectItem>
                <SelectItem value="unimed">Unimed</SelectItem>
                <SelectItem value="amil">Amil</SelectItem>
                <SelectItem value="bradesco">Bradesco Saúde</SelectItem>
                <SelectItem value="sulamerica">SulAmérica</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
            <Textarea
              value={formData.observacoes}
              onChange={(e) => handleInputChange('observacoes', e.target.value)}
              rows={3}
              placeholder="Observações gerais sobre o paciente..."
              data-testid="textarea-modal-observacoes"
            />
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
          <Button variant="outline" onClick={onClose} data-testid="btn-modal-cancelar">
            Cancelar
          </Button>
          <Button onClick={handleSave} className="bg-gray-900 hover:bg-gray-800" data-testid="btn-modal-salvar">
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PacienteModal;
